import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./PendingVerifications.css";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-GB");
}

function aTypeLabel(val) {
  if (val === "N") return "National";
  if (val === "I") return "International";
  return val || "";
}

export default function AchievementsOffice() {
  const navigate = useNavigate();

  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);

  const [employees,   setEmployees]   = useState([]);
  const [categories,  setCategories]  = useState([]);
  const [selectedEmpId,     setSelectedEmpId]     = useState("");
  const [selectedCategory,  setSelectedCategory]  = useState("");
  const [list,        setList]        = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");

  useEffect(() => {
    if (moduleId !== 4) return;
    const empParams = new URLSearchParams({ moduleId });
    fetch(`${API_ROOT}/api/employees/leave-employees-office?${empParams}`)
      .then(r => r.json())
      .then(data => setEmployees(Array.isArray(data.data) ? data.data : []))
      .catch(() => {});
    fetch(`${API_ROOT}/api/employees/achievement-categories?moduleId=${moduleId}`)
      .then(r => r.json())
      .then(data => setCategories(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [moduleId]);

  const onView = async (e) => {
    e.preventDefault();
    if (!selectedEmpId) { setError("Please select an Employee."); return; }
    setError("");
    setLoading(true);
    setHasSearched(true);
    try {
      const params = new URLSearchParams({ moduleId, empId: selectedEmpId });
      if (selectedCategory) params.append("achiCatId", selectedCategory);

      const res  = await fetch(`${API_ROOT}/api/employees/achievement-details?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to load achievement records");
      setList(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      setError(err.message || "Failed to load achievement records");
    } finally {
      setLoading(false);
    }
  };

  if (moduleId !== 4) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <div className="d-flex align-items-center mb-3 gap-2">
        <h3 className="ctitle mb-0">Achievements</h3>
        <button
          className="btn btn-primary btn-sm ms-3"
          onClick={() => navigate("/achievement-by-office-new")}
        >
          New Achievement
        </button>
      </div>

      <form className="row g-2 align-items-end mb-3" onSubmit={onView}>
        <div className="col-sm-5">
          <label className="form-label mb-1">Select Employee</label>
          <select className="form-select form-select-sm" value={selectedEmpId} onChange={e => setSelectedEmpId(e.target.value)}>
            <option value="">-- Select Employee --</option>
            {employees.map(emp => (
              <option key={emp.EmployeeID} value={emp.EmployeeID}>
                {[emp.EmpName, emp.Designation, emp.Department, emp.Mobile].filter(Boolean).join(" - ")}
              </option>
            ))}
          </select>
        </div>

        <div className="col-sm-3">
          <label className="form-label mb-1">Achievement Category</label>
          <select className="form-select form-select-sm" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>
            <option value="">All Categories</option>
            {categories.map(c => (
              <option key={c.AchiCatID} value={c.AchiCatID}>{c.AchiCategory}</option>
            ))}
          </select>
        </div>

        <div className="col-sm-2">
          <button type="submit" className="btn btn-primary btn-sm form-control" disabled={loading}>
            {loading ? "Loading..." : "View"}
          </button>
        </div>
      </form>

      {error && <div className="alert alert-danger">{error}</div>}

      {!hasSearched ? null : loading ? (
        <div className="text-muted">Loading...</div>
      ) : list.length === 0 ? (
        <div className="alert alert-info">No achievement records found.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle pending-verif-table">
            <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
              <tr>
                <th>SNo</th>
                <th>Dept</th>
                <th>Employee</th>
                <th>Mobile</th>
                <th>Title</th>
                <th>Category</th>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Days</th>
                <th>Order Date</th>
                <th>Order No.</th>
                <th className="remarks-cell">Remarks</th>
                <th>Attachment</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row, idx) => (
                <tr key={row.AchievementID ?? idx}>
                  <td>{idx + 1}</td>
                  <td>{row.DepttAbb}</td>
                  <td>{row.EmpName}</td>
                  <td>{row.Mobile}</td>
                  <td>{row.Title}</td>
                  <td>{row.AchiCategory}</td>
                  <td>{aTypeLabel(row.AType)}</td>
                  <td>{formatDate(row.FromDate)}</td>
                  <td>{formatDate(row.ToDate)}</td>
                  <td>{row.NoOfDays}</td>
                  <td>{formatDate(row.OrderDate)}</td>
                  <td>{row.OrderNumber}</td>
                  <td className="remarks-cell">{row.Remarks}</td>
                  <td>
                    {row.SupportDoc ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => window.open(`${API_ROOT}/api/${row.SupportDoc}`, "_blank", "noopener,noreferrer")}
                      >
                        View
                      </button>
                    ) : <span className="text-muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
